import * as React from "react";
import store from "../../store";
import {
  DashboardLayout,
  DashboardLayoutItem,
  UserWorkspaceSettingRole,
  UserWorkspaceSettingScope,
} from "../../types";
import AccountReceivablesWidget from "./Widgets/AccountReceivablesWidget";
import AccountsWidget from "./Widgets/AccountsWidget";
import BoardsWidget from "./Widgets/BoardsWidget";
import ExpensesToProcessWidget from "./Widgets/ExpensesToProcessWidget";
import ExpensesWidget from "./Widgets/ExpensesWidget";
import FinancialPerformanceWidget from "./Widgets/FinancialPerformanceWidget";
import FinancialYearWidget from "./Widgets/FinancialYearWidget";
import MemoWidget from "./Widgets/MemoWidget";
import OverdueInvoicesWidget from "./Widgets/OverdueInvoicesWidget";
import ProjectBudgetHealthWidget from "./Widgets/ProjectBudgetHealthWidget";
import QuarterWidget from "./Widgets/QuarterWidget";
import SendInvoicesWidget from "./Widgets/SendInvoicesWidget";
import TasksWidget from "./Widgets/TasksWidget";
import UnbilledTimeEntriesWidget from "./Widgets/UnbilledTimeEntriesWidget";
import UpcomingRecurringInvoicesWidget from "./Widgets/UpcomingRecurringInvoicesWidget";
import VatWidget from "./Widgets/VatWidget";
import YearlyRevenueGoalWidget from "./Widgets/YearlyRevenueGoalWidget";
import { buildDefaultLayout, hasAccess, resolveLayoutItems } from "./layout/layoutUtils";

export type WidgetType =
  | "financial_performance"
  | "sent_invoices"
  | "financial_year"
  | "quarter"
  | "vat"
  | "memo"
  | "yearly_revenue_goal"
  | "account_receivables"
  | "expenses"
  | "accounts"
  | "tasks"
  | "boards"
  | "overdue_invoices"
  | "upcoming_recurring_invoices"
  | "expenses_to_process"
  | "project_budget_health"
  | "unbilled_time_entries";

export interface WidgetProps {
  currency: string;
  numberFormat: string;
}

export interface WidgetDefinition {
  type: WidgetType;
  /** i18n key */
  title: string;
  component: React.ComponentType<WidgetProps>;
  scopes: UserWorkspaceSettingScope[];
  defaultSize: { w: number; h: number };
  minSize: { minW: number; minH: number };
}

const { REPORTING, BANK, TASK, INVOICE, EXPENSE, PROJECT, TIME_TRACKING } = UserWorkspaceSettingScope;

export const WIDGET_REGISTRY: Record<WidgetType, WidgetDefinition> = {
  financial_performance: {
    type: "financial_performance",
    // i18n.t('Dashboard::Financial performance')
    title: "Dashboard::Financial performance",
    component: FinancialPerformanceWidget,
    scopes: [REPORTING],
    defaultSize: { w: 6, h: 96 },
    minSize: { minW: 4, minH: 96 },
  },
  sent_invoices: {
    type: "sent_invoices",
    // i18n.t('Dashboard::Sent invoices')
    title: "Dashboard::Sent invoices",
    component: SendInvoicesWidget,
    scopes: [REPORTING],
    defaultSize: { w: 6, h: 96 },
    minSize: { minW: 4, minH: 96 },
  },
  financial_year: {
    type: "financial_year",
    // i18n.t('FinancialYearWidget::Financial year')
    title: "FinancialYearWidget::Financial year",
    component: FinancialYearWidget,
    scopes: [REPORTING],
    defaultSize: { w: 6, h: 73 },
    minSize: { minW: 3, minH: 64 },
  },
  quarter: {
    type: "quarter",
    // i18n.t('QuarterWidget::Quarters')
    title: "QuarterWidget::Quarters",
    component: QuarterWidget,
    scopes: [REPORTING],
    defaultSize: { w: 6, h: 73 },
    minSize: { minW: 3, minH: 64 },
  },
  vat: {
    type: "vat",
    // i18n.t('VatWidget::VAT owed')
    title: "VatWidget::VAT owed",
    component: VatWidget,
    scopes: [REPORTING],
    defaultSize: { w: 6, h: 72 },
    minSize: { minW: 3, minH: 64 },
  },
  memo: {
    type: "memo",
    // i18n.t('MemoWidget::Memo')
    title: "MemoWidget::Memo",
    component: MemoWidget,
    scopes: [],
    defaultSize: { w: 6, h: 72 },
    minSize: { minW: 2, minH: 48 },
  },
  yearly_revenue_goal: {
    type: "yearly_revenue_goal",
    // i18n.t('General::Revenue Goal')
    title: "General::Revenue Goal",
    component: YearlyRevenueGoalWidget,
    scopes: [REPORTING],
    defaultSize: { w: 12, h: 80 },
    minSize: { minW: 4, minH: 64 },
  },
  account_receivables: {
    type: "account_receivables",
    // i18n.t('Dashboard::Turnover')
    title: "Dashboard::Turnover",
    component: AccountReceivablesWidget,
    scopes: [REPORTING],
    defaultSize: { w: 6, h: 96 },
    minSize: { minW: 4, minH: 80 },
  },
  expenses: {
    type: "expenses",
    // i18n.t('Dashboard::Expenses')
    title: "Dashboard::Expenses",
    component: ExpensesWidget,
    scopes: [REPORTING],
    defaultSize: { w: 6, h: 96 },
    minSize: { minW: 4, minH: 80 },
  },
  accounts: {
    type: "accounts",
    // i18n.t('AccountsWidget::Accounts')
    title: "AccountsWidget::Accounts",
    component: AccountsWidget,
    scopes: [BANK],
    defaultSize: { w: 6, h: 71 },
    minSize: { minW: 3, minH: 64 },
  },
  tasks: {
    type: "tasks",
    // i18n.t('Dashboard::Tasks')
    title: "Dashboard::Tasks",
    component: TasksWidget,
    scopes: [TASK],
    defaultSize: { w: 6, h: 96 },
    minSize: { minW: 3, minH: 64 },
  },
  boards: {
    type: "boards",
    // i18n.t('Dashboard::Boards')
    title: "Dashboard::Boards",
    component: BoardsWidget,
    scopes: [TASK],
    defaultSize: { w: 6, h: 96 },
    minSize: { minW: 3, minH: 64 },
  },
  overdue_invoices: {
    type: "overdue_invoices",
    // i18n.t('OverdueInvoicesWidget::Overdue invoices')
    title: "OverdueInvoicesWidget::Overdue invoices",
    component: OverdueInvoicesWidget,
    scopes: [INVOICE],
    defaultSize: { w: 6, h: 96 },
    minSize: { minW: 3, minH: 80 },
  },
  upcoming_recurring_invoices: {
    type: "upcoming_recurring_invoices",
    // i18n.t('UpcomingRecurringInvoicesWidget::Upcoming recurring invoices')
    title: "UpcomingRecurringInvoicesWidget::Upcoming recurring invoices",
    component: UpcomingRecurringInvoicesWidget,
    scopes: [INVOICE],
    defaultSize: { w: 6, h: 96 },
    minSize: { minW: 3, minH: 80 },
  },
  expenses_to_process: {
    type: "expenses_to_process",
    // i18n.t('ExpensesToProcessWidget::Expenses to process')
    title: "ExpensesToProcessWidget::Expenses to process",
    component: ExpensesToProcessWidget,
    scopes: [EXPENSE],
    defaultSize: { w: 6, h: 96 },
    minSize: { minW: 3, minH: 80 },
  },
  project_budget_health: {
    type: "project_budget_health",
    // i18n.t('ProjectBudgetHealthWidget::Project budget health')
    title: "ProjectBudgetHealthWidget::Project budget health",
    component: ProjectBudgetHealthWidget,
    scopes: [PROJECT],
    defaultSize: { w: 6, h: 96 },
    minSize: { minW: 3, minH: 80 },
  },
  unbilled_time_entries: {
    type: "unbilled_time_entries",
    // i18n.t('UnbilledTimeEntriesWidget::Uninvoiced time')
    title: "UnbilledTimeEntriesWidget::Uninvoiced time",
    component: UnbilledTimeEntriesWidget,
    scopes: [TIME_TRACKING],
    defaultSize: { w: 6, h: 96 },
    minSize: { minW: 4, minH: 80 },
  },
};

export const DEFAULT_LAYOUT_TEMPLATE: Omit<DashboardLayoutItem, "i">[] = [
  { type: "financial_performance", x: 0, y: 0, w: 6, h: 96 },
  { type: "sent_invoices", x: 6, y: 0, w: 6, h: 96 },
  { type: "financial_year", x: 0, y: 96, w: 6, h: 73 },
  { type: "quarter", x: 6, y: 96, w: 6, h: 73 },
  { type: "vat", x: 0, y: 169, w: 6, h: 72 },
  { type: "memo", x: 6, y: 169, w: 6, h: 72 },
  { type: "yearly_revenue_goal", x: 0, y: 241, w: 12, h: 80 },
  { type: "account_receivables", x: 0, y: 321, w: 6, h: 96 },
  { type: "expenses", x: 6, y: 321, w: 6, h: 96 },
  { type: "accounts", x: 0, y: 417, w: 6, h: 71 },
  { type: "tasks", x: 6, y: 417, w: 6, h: 96 },
  { type: "boards", x: 0, y: 488, w: 6, h: 96 },
];

/** Whether the current user may see the given widget type. Owners pass every scope check. */
export function canUseWidget(type: string): boolean {
  const definition = WIDGET_REGISTRY[type as WidgetType];
  if (!definition) return false;

  const setting = store.getState().authentication.currentUser?.user_workspace_setting;
  const isOwner = setting?.role === UserWorkspaceSettingRole.OWNER;
  const granted = (setting?.scopes ?? []) as string[];

  return hasAccess(definition.scopes, granted, isOwner);
}

export function getDefaultLayout(): DashboardLayoutItem[] {
  return buildDefaultLayout(DEFAULT_LAYOUT_TEMPLATE, canUseWidget);
}

export function getEffectiveLayout(saved: DashboardLayout | null | undefined): DashboardLayoutItem[] {
  if (!saved) return getDefaultLayout();
  return resolveLayoutItems(saved.items, Object.keys(WIDGET_REGISTRY));
}
